/**
 * turnCredentialsClient.ts
 *
 * Module-level cache for ICE/TURN credentials supplied by the Camlin
 * runtime-service `/api/turn-credentials` endpoint. The `useTurnCredentials`
 * React hook (core/hooks/useTurnCredentials.ts) fetches on mount + refresh
 * timer and writes here; `voiceCallFeature.makeCall` reads here when
 * building the `pcConfig.iceServers` array.
 *
 * Why a module-level cache instead of passing through props?
 *   - `makeCall` is synchronous (jssip's `ua.call(target, callOptions)` is
 *     sync), so it can't `await` an HTTP fetch. The hook does the async
 *     work asynchronously and just leaves the result here.
 *   - Every avatar consumer (DM, AgentLink, builder) calls `useHostedLiveController`,
 *     which calls `useTurnCredentials` exactly once per app instance. The
 *     module-level cache is the natural single-process singleton.
 *   - It also means `makeCall` doesn't have to know about the runtime URL
 *     or the consumer; the hook handles all of that. Zero changes to the
 *     consumer surface (the SDK public API stays the same).
 *
 * If the hook hasn't run (or hasn't resolved) by the time `makeCall` is
 * invoked, `getCachedTurnCredentials()` returns the STUN-only fallback
 * with a warning. The previously-leaked hardcoded Twilio NTS tokens were
 * removed in the same commit and are NEVER served from here.
 */

const STUN_ONLY_FALLBACK: RTCIceServer[] = [
  { urls: 'stun:global.stun.twilio.com:3478' },
  { urls: 'stun:stun.l.google.com:19302' },
]

interface CachedTurnCredentials {
  iceServers: RTCIceServer[]
  expiresAt: number
  provider: 'twilio-nts' | 'stun-only'
  warning?: string
}

let cached: CachedTurnCredentials | null = null
let stunOnlyWarningEmitted = false

export function setCachedTurnCredentials(value: CachedTurnCredentials): void {
  cached = value
  if (value.provider === 'twilio-nts') {
    // Once we have real TURN, allow the next STUN-only fallback to log
    // again (e.g. the runtime endpoint goes away). Resetting this flag
    // means each transition into degraded mode produces one warning.
    stunOnlyWarningEmitted = false
  }
}

export function clearCachedTurnCredentials(): void {
  cached = null
  stunOnlyWarningEmitted = false
}

export function getCachedTurnCredentials(): CachedTurnCredentials {
  if (cached && cached.expiresAt > Date.now()) {
    return cached
  }

  if (!stunOnlyWarningEmitted) {
    stunOnlyWarningEmitted = true
    const reason = cached
      ? `cached creds expired at ${new Date(cached.expiresAt).toISOString()}`
      : 'no creds fetched yet (useTurnCredentials hook not mounted, or runtime endpoint unreachable)'
    console.warn(
      `[turnCredentials] Falling back to STUN-only ICE servers — ${reason}. ` +
        `Browsers behind symmetric NAT will fail to establish WebRTC. ` +
        `Ensure runtime-service is reachable and exposes /api/turn-credentials.`,
    )
  }

  return {
    iceServers: STUN_ONLY_FALLBACK,
    expiresAt: Date.now() + 60_000,
    provider: 'stun-only',
    warning: 'STUN-only fallback in use; relay-only retry will fail.',
  }
}

/**
 * Test-only helper. Don't call from production code.
 */
export function _resetTurnCredentialsCacheForTest(): void {
  cached = null
  stunOnlyWarningEmitted = false
}
